import type { ReactNode } from 'react';
import { Box, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';

import type { FoldedPayer, PayerAudience } from '../types';

import { PayerConnectionsFooter } from './PayerConnectionsFooter';
import { PayerRow } from './PayerRow';

/** The design's table header: 11px/700 tracked uppercase on a tinted row. */
const headerCellSx = {
  fontSize: '11px',
  fontWeight: 700,
  letterSpacing: '0.07em',
  textTransform: 'uppercase',
  lineHeight: 1.6,
  color: '#6b6b78',
  backgroundColor: '#fafafd',
  borderColor: '#eeeef4',
  whiteSpace: 'nowrap',
} as const;

export interface PayerTableProps {
  /** Rows already folded and sorted (foldPayers returns them sorted per AC-8). */
  payers: FoldedPayer[];
  liveCount: number;
  unknownCount?: number;
  audience?: PayerAudience;
  /** Per-payer action slot, keyed by payerId — GDN-3406 supplies the links (AC-7). */
  renderAction?: (payer: FoldedPayer) => ReactNode;
  footerRightSlot?: ReactNode;
  /** Reference instant for each row's status age — the read's `checked_at`. */
  asOf?: Date;
}

/**
 * The clinic Payer Connections table (AC-6, AC-8) in the Payer Handoff
 * layout: payer and rails, who has it, what's happening, and how long it has
 * been waiting. Staff keep their raw Stedi column before the waiting time.
 * Presentational only — `payers` is expected pre-sorted by `foldPayers`.
 */
export function PayerTable({
  payers,
  liveCount,
  unknownCount = 0,
  audience,
  renderAction,
  footerRightSlot,
  asOf,
}: PayerTableProps) {
  return (
    <Box
      sx={{
        backgroundColor: '#ffffff',
        border: '1px solid #e2e2ea',
        borderRadius: '10px',
        overflow: 'hidden',
      }}
    >
      <Table size="small" aria-label="Payer connections">
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...headerCellSx, width: '26%' }}>Payer</TableCell>
            <TableCell sx={{ ...headerCellSx, width: 150 }}>Who has it</TableCell>
            <TableCell sx={headerCellSx}>What&apos;s happening</TableCell>
            {audience === 'staff' && <TableCell sx={headerCellSx}>Stedi status</TableCell>}
            <TableCell sx={{ ...headerCellSx, width: 110 }}>Waiting</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {payers.map(payer => (
            <PayerRow
              key={payer.payerId}
              payer={payer}
              audience={audience}
              action={renderAction?.(payer)}
              asOf={asOf}
            />
          ))}
        </TableBody>
      </Table>
      <PayerConnectionsFooter
        liveCount={liveCount}
        unknownCount={unknownCount}
        rightSlot={footerRightSlot}
      />
    </Box>
  );
}
